/**
 * Les gestes de « Gérer une séance » et ses trois écritures, mesurées :
 *  - signaler un retard (`reportAbsence`, `type: delay`) — geste `report_late` ;
 *  - déclarer une absence (`reportAbsence`, `type: absence`) — geste `report_absence` ;
 *  - déplacer un individuel, une séance ou tout le créneau (`moveSession`) — geste `move_session`.
 * Chaque geste s'ouvre (`gesture.opened`) quand le masteur choisit sa situation.
 *
 * Ce que l'ancien écran faisait (`ManageRDVScreen`, `SelectSituationComponent`,
 * `MoveSessionReasonComponent`), et où c'est ici :
 *  - « Plus disponible » sort vers « Libérer des créneaux » (un essai ou un stage : sur l'heure du
 *    live), sinon la boîte du support ; « Autre » : la boîte du support, qui ouvre le WhatsApp ;
 *  - à l'ouverture, un élève en premier mois (`hypercare_block`) : « Ta présence compte » ;
 *  - « Confirmer » le retard : la boîte de ce que les élèves recevront, puis l'envoi ;
 *  - l'accord de l'élève à cocher : « Est-ce que tu confirmes… ? » ; un créneau dont une séance est
 *    déjà replanifiée : « Veux-tu écraser ce changement ? » avant l'envoi ;
 *  - un refus de l'absence : l'impact a changé (il remplace l'aperçu, la boîte nomme les élèves),
 *    ou un élève en premier mois (« Ta présence compte », WhatsApp) ; un autre refus : la phrase du
 *    serveur en tête de la feuille ;
 *  - un succès relit le compte (`getUser`) et montre « Effectué » dans la feuille (l'ancien écran
 *    `congrats`).
 */
import { useNavigation } from "@react-navigation/native"
import moment from "moment"
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react"
import { useDispatch } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { authService } from "../../../../../../data/services/authService"
import { liveService } from "../../../../../../data/services/liveService"
import { type TutorManageSessionFlowProps } from "../../../../../../design-system/templates/TutorManageSessionFlow"
import {
    dayTimeOf,
    hypercareOf,
    makeupOptions,
    rescheduleReasons,
    skipsMakeup,
    slotOf,
    toUIAbsenceCredits,
    toUIAbsenceImpact,
    TUTOR_ABSENCE_REASON,
    tutorChangeIds,
    type ApiAbsenceImpactStudent,
} from "../../../../adapters/tutor/Lives/tutorManageSessionAdapter"
import { TUTOR_CHARTER_URL } from "../../../../adapters/tutor/shared/tutorLinks"
import { type TutorManageSessionViewModel } from "../../../../view-models/tutor/Lives/tutorManageSessionViewModel"
import { type UITutorManageSituation } from "../../../types/tutorLives"
import { openExternalUrl } from "../../../hooks/openExternalUrl"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { useBackOr } from "../../../hooks/useBackOr"
import { useOpenTutorSupport } from "../useOpenTutorSupport"
import { initialTutorManageSessionUi, tutorManageSessionReducer } from "./tutorManageSessionUiState"
import { type useTutorManageSessionData } from "./useTutorManageSessionData"

type StackNavigation = { push: (route: string, params?: object) => void }

/** L'écran d'où partent les gestes, en propriété `surface` de leur mesure. */
const SURFACE = "tutor_manage_session"

/** Un refus sans phrase du serveur. */
const GENERIC_ERROR = "Une erreur est survenue. Réessaie dans un instant."

/** La phrase du refus « premier mois » quand le serveur n'en donne pas. */
const HYPERCARE_REFUSED_FALLBACK = "Tu as un élève en premier mois dans cette session."

type AbsenceRefusal = {
    absence_impact?: ApiAbsenceImpactStudent[] | null
    has_hypercare_students?: boolean
    message?: string
    support_whatsapp_url?: string | null
}

export type TutorManageSessionReads = ReturnType<typeof useTutorManageSessionData>

export function useTutorManageSessionState(vm: TutorManageSessionViewModel, reads: TutorManageSessionReads) {
    const navigation = useNavigation() as unknown as StackNavigation
    const dispatch = useDispatch()
    const [ui, dispatchUi] = useReducer(tutorManageSessionReducer, initialTutorManageSessionUi)
    const { managed } = vm
    /** Où mène « Contacter sur WhatsApp » de la boîte ouverte : le lien du serveur, sinon celui de l'équipe. */
    const whatsappRef = useRef<string | undefined>(undefined)
    const openSupport = useOpenTutorSupport()

    const close = useBackOr("menu")

    // Le compte est là mais la séance n'y est plus (passée, retirée) : rien à gérer, on revient.
    const missing = !vm.loading && managed == null
    useEffect(() => {
        if (missing) close()
    }, [missing, close])

    // ── Ce que les lectures donnent ──
    const impact = useMemo(
        () => ui.impactOverride ?? (reads.impactStudents != null ? toUIAbsenceImpact(reads.impactStudents) : null),
        [ui.impactOverride, reads.impactStudents]
    )
    const absenceDate = managed?.occurrenceDate ?? ""
    const credits = useMemo(
        () => (managed != null ? toUIAbsenceCredits(reads.summary, absenceDate) : null),
        [managed, reads.summary, absenceDate]
    )
    const makeups = useMemo(
        () => (managed != null ? makeupOptions(vm.liveSessions, absenceDate, managed.raw) : []),
        [managed, vm.liveSessions, absenceDate]
    )
    const skipMakeup = skipsMakeup(makeups, absenceDate, impact)

    // À l'ouverture : la boîte « Ta présence compte » quand le décompte la demande (une fois).
    const hypercareShownRef = useRef(false)
    useEffect(() => {
        const hypercare = hypercareOf(reads.summary)
        if (hypercare == null || hypercareShownRef.current) return
        hypercareShownRef.current = true
        whatsappRef.current = hypercare.url
        dispatchUi({ type: "OPEN_DIALOG", dialog: { kind: "hypercare", message: hypercare.message } })
    }, [reads.summary])

    // « Je dois m'absenter » touché avant le décompte : l'étape s'ouvre quand il arrive.
    useEffect(() => {
        if (ui.awaitingAbsence && reads.summaryRead) dispatchUi({ type: "GO", step: "absenceConfirm" })
    }, [ui.awaitingAbsence, reads.summaryRead])

    // ── Les écritures ──
    const afterSuccess = useCallback(() => {
        dispatch(apiRequest(authService.getUser) as never)
        dispatchUi({ type: "DONE" })
    }, [dispatch])
    const onPlainError = useCallback((message: string) => {
        dispatchUi({ type: "SET", patch: { errorMessage: message } })
    }, [])

    const delay = useApiMutation({
        service: liveService.reportAbsence,
        fallback: GENERIC_ERROR,
        gesture: "report_late",
        surface: SURFACE,
        skipWhilePending: true,
        onSuccess: afterSuccess,
        onError: onPlainError,
    })
    const absence = useApiMutation({
        service: liveService.reportAbsence,
        fallback: GENERIC_ERROR,
        gesture: "report_absence",
        surface: SURFACE,
        skipWhilePending: true,
        onSuccess: afterSuccess,
        onError: (message, error) => {
            const refusal = error as AbsenceRefusal | null
            if (refusal?.absence_impact != null) {
                dispatchUi({ type: "IMPACT_CHANGED", impact: toUIAbsenceImpact(refusal.absence_impact) })
                return
            }
            if (refusal?.has_hypercare_students) {
                whatsappRef.current = refusal.support_whatsapp_url ?? undefined
                dispatchUi({
                    type: "OPEN_DIALOG",
                    dialog: { kind: "hypercareRefused", message: refusal.message || HYPERCARE_REFUSED_FALLBACK },
                })
                return
            }
            onPlainError(message)
        },
    })
    const move = useApiMutation({
        service: liveService.moveSession,
        fallback: GENERIC_ERROR,
        gesture: "move_session",
        surface: SURFACE,
        skipWhilePending: true,
        onSuccess: afterSuccess,
        onError: onPlainError,
    })

    const send = useCallback(
        (kind: "delay" | "absence" | "move") => {
            if (managed == null) return
            dispatchUi({ type: "SET", patch: { errorMessage: null } })
            const id = managed.session.id
            if (kind === "delay") {
                delay.start({ id }, { type: "delay", late_minutes: ui.lateMinutes })
                return
            }
            if (kind === "absence") {
                const selected = skipMakeup
                    ? []
                    : makeups.filter((option) => ui.makeupIds.includes(option.id)).map((option) => option.payload)
                absence.start(
                    { id },
                    {
                        type: "absence",
                        reason: TUTOR_ABSENCE_REASON,
                        tutor_message: ui.message,
                        instructions: ui.instructions,
                        parent_notification_validation: ui.excuseConfirmed,
                        other_occurrences: [],
                        selected_makeup_occurrences: selected,
                        acknowledged_tutor_change_ids: tutorChangeIds(impact),
                    }
                )
                return
            }
            const reason = ui.reasonId ?? ""
            move.start(
                { id },
                ui.moveKind === "one_occurrence"
                    ? {
                          type: "one_occurrence",
                          new_date: ui.newDate,
                          previous_date: managed.occurrenceDate,
                          reason,
                          message_to_parent: ui.parentMessage,
                          student_validation: ui.studentValidated,
                      }
                    : {
                          type: "all_occurrences",
                          new_date: dayTimeOf(ui.newSlot),
                          reason,
                          message_to_parent: ui.parentMessage,
                          student_validation: ui.studentValidated,
                      }
            )
        },
        [managed, ui, skipMakeup, makeups, impact, delay, absence, move]
    )

    // ── La situation ──
    const onSelectSituation = useCallback(
        (situation: UITutorManageSituation) => {
            if (managed == null) return
            switch (situation) {
                case "delay":
                    delay.gesture.opened()
                    dispatchUi({ type: "GO", step: "delayMinutes" })
                    return
                case "absence":
                    absence.gesture.opened()
                    if (reads.summaryRead) dispatchUi({ type: "GO", step: "absenceConfirm" })
                    else dispatchUi({ type: "AWAIT_ABSENCE" })
                    return
                case "unavailable":
                    if (managed.release != null) {
                        navigation.push("tutor/liberer-creneaux", managed.release)
                        return
                    }
                    dispatchUi({ type: "OPEN_DIALOG", dialog: { kind: "support", reason: "unavailable" } })
                    return
                case "other":
                    dispatchUi({ type: "OPEN_DIALOG", dialog: { kind: "support", reason: "other" } })
                    return
                case "one_occurrence":
                case "all_occurrences":
                    move.gesture.opened()
                    dispatchUi({
                        type: "START_MOVE",
                        moveKind: situation,
                        newDate: managed.occurrenceDate,
                        newSlot: slotOf(managed.raw.day_time),
                    })
                    return
            }
        },
        [managed, delay.gesture, absence.gesture, move.gesture, reads.summaryRead, navigation]
    )

    const onContinue = useCallback(() => {
        switch (ui.step) {
            case "delayMinutes":
                dispatchUi({ type: "GO", step: "delayConfirm" })
                return
            case "absenceConfirm":
                dispatchUi({ type: "GO", step: skipMakeup ? "absenceMessage" : "makeup" })
                return
            case "makeup":
                dispatchUi({ type: "GO", step: "absenceMessage" })
                return
            case "newDate":
                dispatchUi({ type: "GO", step: "rescheduleReason" })
                return
        }
    }, [ui.step, skipMakeup])

    const onSubmit = useCallback(() => {
        if (ui.step === "delayConfirm") {
            dispatchUi({ type: "OPEN_DIALOG", dialog: { kind: "confirmDelay" } })
        } else if (ui.step === "absenceMessage") {
            send("absence")
        } else if (ui.step === "rescheduleReason") {
            if (ui.moveKind === "all_occurrences" && managed?.rescheduledDate != null) {
                dispatchUi({
                    type: "OPEN_DIALOG",
                    dialog: { kind: "overwriteReschedule", rescheduledDate: managed.rescheduledDate },
                })
            } else send("move")
        }
    }, [ui.step, ui.moveKind, managed, send])

    const onDialogConfirm = useCallback(() => {
        const dialog = ui.dialog
        dispatchUi({ type: "CLOSE_DIALOG" })
        switch (dialog?.kind) {
            case "hypercare":
            case "hypercareRefused":
                openSupport(whatsappRef.current)
                return
            case "support":
                openSupport()
                return
            case "confirmDelay":
                send("delay")
                return
            case "confirmStudentValidation":
                dispatchUi({ type: "SET", patch: { studentValidated: true } })
                return
            case "overwriteReschedule":
                send("move")
                return
        }
    }, [ui.dialog, openSupport, send])

    const reasons = rescheduleReasons(vm.reschedulingReasons, ui.moveKind)

    const flow: Omit<TutorManageSessionFlowProps, "testID"> | null =
        managed == null
            ? null
            : {
                  session: managed.session,
                  isIndividual: managed.isIndividual,
                  slotLabel: managed.slotLabel,
                  firstStudentName: managed.firstStudentName,
                  visible: true,
                  onClose: close,
                  step: ui.step,
                  onBack: () => dispatchUi({ type: "BACK" }),
                  situation: {
                      canReportDelay: managed.canReportDelay,
                      isTrial: managed.isTrial,
                      substitutionAccepted: managed.substitutionAccepted,
                      onSelect: onSelectSituation,
                  },
                  delay: {
                      minutes: ui.lateMinutes,
                      onSelect: (minutes) => dispatchUi({ type: "SET", patch: { lateMinutes: minutes } }),
                  },
                  absence: {
                      impact,
                      credits,
                      confirmed: ui.absenceConfirmed,
                      onToggleConfirmed: (next) => dispatchUi({ type: "SET", patch: { absenceConfirmed: next } }),
                      onOpenCharter: () => openExternalUrl(TUTOR_CHARTER_URL),
                  },
                  makeup: {
                      options: makeups,
                      selectedIds: ui.makeupIds,
                      onToggle: (id) => dispatchUi({ type: "TOGGLE_MAKEUP", id }),
                      tooClose: moment(absenceDate).diff(moment(), "minutes") < 30,
                  },
                  message: {
                      message: ui.message,
                      onChangeMessage: (text) => dispatchUi({ type: "SET", patch: { message: text } }),
                      instructions: ui.instructions,
                      onChangeInstructions: (text) => dispatchUi({ type: "SET", patch: { instructions: text } }),
                      excuseConfirmed: ui.excuseConfirmed,
                      onToggleExcuseConfirmed: (next) => dispatchUi({ type: "SET", patch: { excuseConfirmed: next } }),
                  },
                  newDate: {
                      moveKind: ui.moveKind,
                      newDate: ui.newDate,
                      onChangeNewDate: (value) => dispatchUi({ type: "SET", patch: { newDate: value } }),
                      newSlot: ui.newSlot,
                      onChangeNewSlot: (slot) => dispatchUi({ type: "SET", patch: { newSlot: slot } }),
                  },
                  reschedule: {
                      reasons,
                      reasonId: ui.reasonId,
                      onSelectReason: (id) => dispatchUi({ type: "SET", patch: { reasonId: id } }),
                      parentMessage: ui.parentMessage,
                      onChangeParentMessage: (text) => dispatchUi({ type: "SET", patch: { parentMessage: text } }),
                      studentValidated: ui.studentValidated,
                      onToggleStudentValidated: () => {
                          if (ui.studentValidated) dispatchUi({ type: "SET", patch: { studentValidated: false } })
                          else dispatchUi({ type: "OPEN_DIALOG", dialog: { kind: "confirmStudentValidation" } })
                      },
                  },
                  onContinue,
                  onSubmit,
                  submitting: delay.requesting || absence.requesting || move.requesting,
                  errorMessage: ui.errorMessage ?? undefined,
                  dialog: ui.dialog,
                  onDialogConfirm,
                  onDialogClose: () => dispatchUi({ type: "CLOSE_DIALOG" }),
              }

    return flow
}
